import React, { useCallback, useEffect, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import {
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { notifications } from '@mantine/notifications'
import { createCouponCode, deactivateCouponCode, listCouponCodes } from '../../data/admin.service'
import { CouponCode } from '../../models/Admin/CouponCode'

const CouponCodes: React.FC = () => {
  const { instance } = useMsal()
  const [coupons, setCoupons] = useState<CouponCode[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [deactivatingId, setDeactivatingId] = useState<number | null>(null)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [newCode, setNewCode] = useState('')
  const [newExpiresAt, setNewExpiresAt] = useState<Date | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)

  const fetchCoupons = useCallback(async () => {
    try {
      const data = await listCouponCodes(instance)
      setCoupons(data)
    } catch (error) {
      console.error('Error fetching coupon codes:', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to fetch coupon codes. Please try again.',
        color: 'red',
        autoClose: true,
      })
    } finally {
      setIsLoading(false)
    }
  }, [instance])

  useEffect(() => {
    fetchCoupons()
  }, [fetchCoupons])

  const handleOpenCreateModal = () => {
    setNewCode('')
    setNewExpiresAt(null)
    setCreateError(null)
    setCreateModalOpen(true)
  }

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false)
    setCreateError(null)
  }

  const handleCreate = async () => {
    if (!newCode.trim()) {
      setCreateError('Code is required.')
      return
    }
    setIsCreating(true)
    setCreateError(null)
    try {
      const created = await createCouponCode(instance, newCode.trim(), newExpiresAt)
      setCoupons((prev) => [created, ...prev])
      setCreateModalOpen(false)
      notifications.show({
        title: 'Success',
        message: `Coupon code "${created.code}" created successfully.`,
        color: 'green',
        autoClose: true,
      })
    } catch (error: any) {
      const message =
        error?.response?.data?.message ??
        error?.message ??
        'Failed to create coupon code. The code may already exist.'
      setCreateError(message)
    } finally {
      setIsCreating(false)
    }
  }

  const handleDeactivate = async (id: number) => {
    setDeactivatingId(id)
    try {
      const updated = await deactivateCouponCode(instance, id)
      setCoupons((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
      notifications.show({
        title: 'Success',
        message: `Coupon code "${updated.code}" has been deactivated.`,
        color: 'green',
        autoClose: true,
      })
    } catch (error) {
      console.error('Error deactivating coupon code:', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to deactivate coupon code. Please try again.',
        color: 'red',
        autoClose: true,
      })
    } finally {
      setDeactivatingId(null)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  if (isLoading) {
    return (
      <Box py={24}>
        <Group justify='center' mt={50}>
          <Loader size='lg' />
        </Group>
      </Box>
    )
  }

  return (
    <Box py={24}>
      <Group justify='space-between' mb={24}>
        <Title>Coupon Codes</Title>
        <Button onClick={handleOpenCreateModal}>Create Coupon</Button>
      </Group>

      {coupons.length === 0 ? (
        <Paper withBorder p={36} mt={32}>
          <Text ta='center'>No coupon codes yet. Create one above.</Text>
        </Paper>
      ) : (
        <Paper withBorder radius={16} p={0} style={{ overflow: 'hidden' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Code</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Redemptions</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th>Expires</Table.Th>
                <Table.Th>Action</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {coupons.map((coupon) => (
                <Table.Tr key={coupon.id}>
                  <Table.Td>
                    <Text fw={600}>{coupon.code}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={coupon.isActive ? 'green' : 'gray'}>
                      {coupon.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text>{coupon.redemptionCount}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size='sm'>{formatDate(coupon.createdAt)}</Text>
                  </Table.Td>
                  <Table.Td>
                    {coupon.expiresAt ? (
                      <Text size='sm'>{formatDate(coupon.expiresAt)}</Text>
                    ) : (
                      <Text c='dimmed'>Never</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size='xs'
                      color='red'
                      variant='outline'
                      disabled={!coupon.isActive || deactivatingId === coupon.id}
                      loading={deactivatingId === coupon.id}
                      onClick={() => handleDeactivate(coupon.id)}
                    >
                      Deactivate
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <Modal
        opened={createModalOpen}
        onClose={handleCloseCreateModal}
        title='Create Coupon Code'
        centered
      >
        <Stack gap='md'>
          <TextInput
            label='Code'
            placeholder='e.g. SUMMER25'
            value={newCode}
            onChange={(e) => setNewCode(e.currentTarget.value.toUpperCase())}
            required
          />
          <DatePickerInput
            label='Expiry Date (optional)'
            placeholder='Pick a date'
            value={newExpiresAt}
            onChange={setNewExpiresAt}
            clearable
            minDate={new Date()}
          />
          {createError && (
            <Text c='red' size='sm'>
              {createError}
            </Text>
          )}
          <Group justify='flex-end' mt='sm'>
            <Button variant='default' onClick={handleCloseCreateModal} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={isCreating}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}

export default CouponCodes
